import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import { Tooltip } from '../ui/tooltip';
import {
  SUBSCRIPTION_STATUS_LABELS,
  SUBSCRIPTION_STATUS_TONES,
  type SubscriptionStatusTone,
} from '@/utils/subscriptionStatus';

const TONE_DOT: Record<SubscriptionStatusTone, string> = {
  emerald: 'bg-emerald-400',
  blue: 'bg-blue-400',
  amber: 'bg-amber-400',
  red: 'bg-red-400',
};

function formatDate(date: Date | string): string {
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

function formatDateFull(date: Date | string): string {
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

function UsageTooltipContent() {
  const { subscription, personalUsage } = useSubscriptionStore();
  if (!subscription) return null;

  const enc = personalUsage?.myUsage;
  const orgLimits = personalUsage?.orgLimits;
  const orgUsage = personalUsage?.orgUsage;
  const period = personalUsage?.currentPeriod;

  // Personal encounters display
  let personalEncText = '';
  if (enc) {
    if (enc.personalEncounterLimit > 0) {
      personalEncText = `${enc.encounters} / ${enc.personalEncounterLimit} encounters (${enc.personalEncounterPercentUsed}%)`;
    } else {
      personalEncText = `${enc.encounters} encounters (no limit)`;
    }
  }

  // Org encounters display
  let orgEncText = '';
  if (orgUsage && orgLimits) {
    if (orgLimits.isUnlimited.encounters) {
      orgEncText = `${orgUsage.encountersUsed} encounters (unlimited)`;
    } else {
      orgEncText = `${orgUsage.encountersUsed} / ${orgLimits.encountersPerMonth} encounters (${orgUsage.encountersPercent}%)`;
    }
  }

  // Org minutes display
  let orgMinText = '';
  if (orgUsage && orgLimits) {
    if (orgLimits.isUnlimited.minutes) {
      orgMinText = `${orgUsage.minutesUsed} min (unlimited)`;
    } else {
      orgMinText = `${orgUsage.minutesUsed} / ${orgLimits.transcriptionMinutes} min (${orgUsage.minutesPercent}%)`;
    }
  }

  return (
    <div className="w-56 text-xs space-y-2" onClick={e => e.stopPropagation()}>
      {/* Plan & Period */}
      <div>
        {subscription.planName && (
          <div className="font-medium text-gray-200">{subscription.planName}</div>
        )}
        {period && (
          <div className="text-gray-400">
            {formatDateFull(period.start)} &ndash; {formatDateFull(period.end)}
          </div>
        )}
      </div>

      {/* Personal Usage */}
      {enc && (
        <div className="border-t border-gray-700 pt-2">
          <div className="font-medium text-gray-300 mb-1">Your Usage</div>
          <div className={enc.isOverPersonalLimit ? 'text-red-400' : 'text-gray-300'}>
            {personalEncText}
          </div>
          <div className="text-gray-400">{enc.minutes} min transcription</div>
        </div>
      )}

      {/* Organization Usage */}
      {orgUsage && orgLimits && (
        <div className="border-t border-gray-700 pt-2">
          <div className="font-medium text-gray-300 mb-1">Organization</div>
          <div className={orgUsage.isInOverage ? 'text-amber-400' : 'text-gray-300'}>
            {orgEncText}
          </div>
          <div className="text-gray-400">{orgMinText}</div>
        </div>
      )}

      {/* Renewal */}
      {subscription.status === 'active' && subscription.period.currentPeriodEnd && (
        <div className="border-t border-gray-700 pt-2 text-gray-400">
          Renews {formatDate(subscription.period.currentPeriodEnd)}
        </div>
      )}

      {/* Over limit warning */}
      {enc?.isOverPersonalLimit && (
        <div className="border-t border-gray-700 pt-2 text-red-400 font-medium">
          Over personal encounter limit
        </div>
      )}
    </div>
  );
}

export const SubscriptionStatusBar: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { subscription, personalUsage } = useSubscriptionStore();

  if (!(user?.role === 'admin' || user?.role === 'operator') || !subscription) {
    return null;
  }

  // The status meanings are shared with the console usage table; only the dot classes are local.
  const tone = SUBSCRIPTION_STATUS_TONES[subscription.status];
  const dot = tone ? TONE_DOT[tone] : 'bg-slate-400';
  const label = SUBSCRIPTION_STATUS_LABELS[subscription.status] || 'Unknown';

  let detail = '';
  if (subscription.status === 'trialing' && subscription.period.trialEndsAt) {
    const daysLeft = Math.max(0, Math.ceil((new Date(subscription.period.trialEndsAt).getTime() - Date.now()) / 86400000));
    detail = `${daysLeft}d remaining`;
  } else if (subscription.status === 'active' && subscription.period.currentPeriodEnd) {
    detail = `Renews ${formatDate(subscription.period.currentPeriodEnd)}`;
  } else if (subscription.status === 'suspended') {
    detail = 'Contact support';
  }

  const enc = personalUsage?.myUsage;
  const orgLimits = personalUsage?.orgLimits;
  const orgUsage = personalUsage?.orgUsage;
  const encounterText = enc
    ? enc.personalEncounterLimit > 0
      ? `${enc.encounters} / ${enc.personalEncounterLimit} encounters`
      : orgLimits && !orgLimits.isUnlimited?.encounters && orgUsage
        ? `${orgUsage.encountersUsed} / ${orgLimits.encountersPerMonth} encounters`
        : `${enc.encounters} encounters`
    : null;

  const handleNavigate = () => {
    // Only admins can access org-wide usage dashboard; operators/staff go to personal usage
    if (user?.role === 'admin') {
      navigate('/organization/usage');
    } else {
      navigate('/my-usage');
    }
  };

  return (
    <div
      className="flex items-center gap-2.5 text-sm px-1 py-1.5 cursor-pointer hover:opacity-80 transition-opacity"
      onClick={handleNavigate}
    >
      <span className={`w-2 h-2 rounded-full ${dot} flex-shrink-0`} />
      <span className="font-medium text-gray-700">{label}</span>
      {subscription.planName && (
        <>
          <span className="text-gray-300">&middot;</span>
          <span className="text-gray-500">{subscription.planName}</span>
        </>
      )}
      {encounterText && (
        <>
          <span className="text-gray-300">&middot;</span>
          <Tooltip content={<UsageTooltipContent />} placement="bottom" delay={150}>
            <span className="text-gray-500 underline decoration-dotted underline-offset-2 decoration-gray-400">
              {encounterText}
            </span>
          </Tooltip>
        </>
      )}
      {detail && (
        <span className="ml-auto text-xs text-gray-400">{detail}</span>
      )}
    </div>
  );
};
